import { useMemo, useState } from "react";
import { usePhases, useCriteria, useDeliverables } from "@/hooks/useLive";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { PhaseStatusBadge } from "@/components/ui/StatusBadge";
import { ChevronDown, ChevronLeft, ChevronRight, Flag, Milestone, CalendarDays, List, CheckCircle2, Clock, Circle, Ban } from "lucide-react";
import { cn, parseLaunchDate } from "@/lib/utils";
import { CATEGORIES, STATUS_MAP } from "@/data/categories";
import { Avatar } from "@/components/ui/Avatar";
import type { Phase, Criterion, CriterionStatus, Deliverable, Category, Status } from "@/lib/types";

const CRIT_META: Record<CriterionStatus, { label: string; Icon: typeof CheckCircle2; color: string; bg: string }> = {
  not_started: { label: "Not started", Icon: Circle, color: "text-slate-500", bg: "bg-slate-50 border-slate-200" },
  in_progress: { label: "In progress", Icon: Clock, color: "text-sky-700", bg: "bg-sky-50 border-sky-200" },
  approved:    { label: "Approved", Icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  blocked:     { label: "Blocked", Icon: Ban, color: "text-rose-700", bg: "bg-rose-50 border-rose-200" },
};

/** Canonical 5-phase spine — matches the phase values used on deliverables. */
const PHASE_SPINE: { name: string; phaseId: string; gateId: string | null }[] = [
  { name: "Alpha", phaseId: "alpha", gateId: "gate1" },
  { name: "Alpha+", phaseId: "alpha_plus", gateId: "gate2" },
  { name: "Closed Beta 1", phaseId: "closed_beta_1", gateId: "gate3" },
  { name: "Closed Beta 2", phaseId: "closed_beta_2", gateId: "gate4" },
  { name: "GA", phaseId: "ga", gateId: null },
];

function PhaseDetailRows({ phase }: { phase: Phase }) {
  const rows: Array<{ label: string; value?: string; accent?: string }> = [
    { label: "Audience", value: phase.audience },
    { label: "Purpose", value: phase.purpose },
    { label: "Risks / blockers", value: phase.risksBlockers, accent: phase.risksBlockers?.startsWith("FLAG:") ? "text-destructive" : undefined },
    { label: "Notes", value: phase.notes },
  ];
  const shown = rows.filter((r) => r.value);
  if (shown.length === 0) {
    return <p className="text-caption italic text-muted-foreground">No extra details on this phase.</p>;
  }
  return (
    <div className="space-y-2">
      {shown.map((r) => (
        <div key={r.label} className="flex gap-3">
          <div className="w-28 shrink-0 text-caption font-semibold text-muted-foreground">{r.label}</div>
          <div className={cn("flex-1 text-caption leading-relaxed", r.accent ?? "text-foreground/90")}>{r.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ─────────── Task row (used inside a category, inside a phase) ─────────── */
function TaskRow({ d }: { d: Deliverable }) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<Status>(d.status);
  const [saving, setSaving] = useState(false);
  const meta = STATUS_MAP[local];
  const handleStatus = async (next: Status) => {
    const prev = local;
    setLocal(next);
    setSaving(true);
    try {
      await fetch(`/api/deliverables/${d.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
        credentials: "include",
      });
      qc.invalidateQueries({ queryKey: ["deliverables"] });
    } catch { setLocal(prev); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-3 rounded-md bg-muted/20 px-3 py-2 hover:bg-muted/40">
      <div className="min-w-0 flex-1">
        <div className="truncate text-body font-medium text-foreground">{d.name}</div>
        <div className="mt-0.5 flex items-center gap-2 text-micro text-muted-foreground">
          <Avatar name={d.owner} size="xs" />
          <span>{d.owner || "—"}</span>
          {d.due && <span>· {d.due}</span>}
          {d.flag && <span className="text-destructive">· {d.flag}</span>}
        </div>
      </div>
      <select
        value={local}
        onChange={(e) => handleStatus(e.target.value as Status)}
        disabled={saving}
        className={cn(
          "shrink-0 rounded-full border border-transparent px-2.5 py-0.5 text-micro font-medium focus:outline-none",
          meta.bg, meta.color, saving && "opacity-50"
        )}
      >
        {(Object.keys(STATUS_MAP) as Status[]).map((s) => (
          <option key={s} value={s}>{STATUS_MAP[s].label}</option>
        ))}
      </select>
    </div>
  );
}

function CategoryBlock({ category, items }: { category: Category; items: Deliverable[] }) {
  const [open, setOpen] = useState(false);
  const meta = CATEGORIES[category];
  const done = items.filter((d) => d.status === "complete").length;
  const blocked = items.filter((d) => d.status === "blocked").length;
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/30"
        style={{ borderLeft: `3px solid ${meta.color}` }}
      >
        <span className="text-body font-semibold text-foreground">{meta.label}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-micro tabular text-muted-foreground">
          {done}/{items.length}
        </span>
        {blocked > 0 && (
          <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-micro font-medium text-destructive">
            {blocked} blocked
          </span>
        )}
        <ChevronDown size={14} className={cn("ml-auto text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="space-y-1 border-t border-border bg-muted/10 p-2">
          {items.map((d) => <TaskRow key={d.id} d={d} />)}
        </div>
      )}
    </div>
  );
}

function CategoriesBucket({
  totalCategories,
  totalTasks,
  children,
}: {
  totalCategories: number;
  totalTasks: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/30"
      >
        <div className="text-micro font-semibold uppercase tracking-wider text-muted-foreground">
          Categories · tasks from the Deliverables Tracker
        </div>
        <span className="rounded-full bg-muted px-2 py-0.5 text-micro tabular text-muted-foreground">
          {totalCategories} {totalCategories === 1 ? "category" : "categories"} · {totalTasks} {totalTasks === 1 ? "task" : "tasks"}
        </span>
        <ChevronDown size={14} className={cn("ml-auto text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="border-t border-border bg-muted/10 p-2">
          {children}
        </div>
      )}
    </div>
  );
}

function PhaseContainer({
  phase,
  tasks,
  gate,
  criteria,
  isAdmin,
  onApprove,
  defaultOpen,
}: {
  phase: Phase | undefined;
  tasks: Deliverable[];
  gate: Phase | undefined;
  criteria: Criterion[];
  isAdmin: boolean;
  onApprove: (gateId: string) => void;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isActive = phase?.status === "active";
  const isComplete = phase?.status === "complete";
  const done = tasks.filter((t) => t.status === "complete").length;
  const blocked = tasks.filter((t) => t.status === "blocked").length;

  const grouped = useMemo(() => {
    const m = new Map<Category, Deliverable[]>();
    for (const t of tasks) {
      if (!m.has(t.category)) m.set(t.category, []);
      m.get(t.category)!.push(t);
    }
    return (Object.keys(CATEGORIES) as Category[])
      .filter((c) => m.has(c))
      .map((c) => ({ category: c, items: m.get(c)! }));
  }, [tasks]);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border bg-card transition-shadow",
        isActive ? "border-sky-300 shadow-card-hover" : isComplete ? "border-emerald-200" : "border-border"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30"
      >
        <span className={cn(
          "h-2.5 w-2.5 shrink-0 rounded-full",
          isComplete ? "bg-emerald-500" : isActive ? "bg-sky-500 ring-2 ring-sky-500/30" : "bg-slate-300"
        )} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className={cn(
              "text-h4 font-semibold",
              isActive ? "text-sky-700" : isComplete ? "text-emerald-800" : "text-foreground"
            )}>
              {phase?.name ?? "—"}
            </span>
            <span className="text-caption tabular text-muted-foreground">{phase?.date}</span>
          </div>
          <div className="mt-0.5 text-micro text-muted-foreground">
            {tasks.length} task{tasks.length === 1 ? "" : "s"} · {done} complete
            {blocked > 0 && <span className="text-destructive"> · {blocked} blocked</span>}
          </div>
        </div>
        {phase && <PhaseStatusBadge status={phase.status} />}
        <ChevronDown size={16} className={cn("text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="space-y-4 border-t border-border bg-muted/10 px-3 py-4 md:px-4">
          {/* Optional phase-level details */}
          {(phase?.audience || phase?.purpose || phase?.notes) && (
            <div className="rounded-lg border border-border bg-card px-3 py-2">
              <PhaseDetailRows phase={phase!} />
            </div>
          )}
          {/* Categories — single collapsible bucket; expand to see the
               per-category sub-collapsibles. */}
          <CategoriesBucket totalCategories={grouped.length} totalTasks={tasks.length}>
            {grouped.length === 0 ? (
              <p className="rounded-md border border-dashed border-border bg-card px-3 py-4 text-center text-caption text-muted-foreground">
                No tasks mapped to this phase yet.
              </p>
            ) : (
              <div className="space-y-2">
                {grouped.map((g) => (
                  <CategoryBlock key={g.category} category={g.category} items={g.items} />
                ))}
              </div>
            )}
          </CategoriesBucket>
          {/* Nested gate at the end of the phase */}
          {gate && (
            <div>
              <div className="mb-2 text-micro font-semibold uppercase tracking-wider text-amber-800">
                Phase gate
              </div>
              <GateBlock gate={gate} criteria={criteria} isAdmin={isAdmin} onApprove={onApprove} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const CRIT_STATUSES: CriterionStatus[] = ["not_started", "in_progress", "approved", "blocked"];

function CriterionLine({ c, canEdit }: { c: Criterion; canEdit: boolean }) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<CriterionStatus>(c.status);
  const [saving, setSaving] = useState(false);
  const meta = CRIT_META[local];

  const handleStatus = async (next: CriterionStatus) => {
    const prev = local;
    setLocal(next);
    setSaving(true);
    try {
      await fetch(`/api/criteria/${c.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
        credentials: "include",
      });
      qc.invalidateQueries({ queryKey: ["criteria"] });
    } catch {
      setLocal(prev);
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2">
      <meta.Icon size={13} className={meta.color} />
      <div className="min-w-0 flex-1">
        <div className="text-caption font-medium text-foreground">{c.title}</div>
        {(c.owner || c.notes) && (
          <div className="mt-0.5 text-micro text-muted-foreground">
            {c.owner && <span>Owner · {c.owner}</span>}
            {c.owner && c.notes && <span> · </span>}
            {c.notes && <span className="italic">{c.notes}</span>}
          </div>
        )}
      </div>
      {canEdit ? (
        <select
          value={local}
          onChange={(e) => handleStatus(e.target.value as CriterionStatus)}
          disabled={saving}
          className={cn(
            "shrink-0 rounded-full border px-2.5 py-0.5 text-micro font-medium focus:outline-none",
            meta.bg, meta.color, saving && "opacity-50"
          )}
        >
          {CRIT_STATUSES.map((s) => <option key={s} value={s}>{CRIT_META[s].label}</option>)}
        </select>
      ) : (
        <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-micro font-medium", meta.bg, meta.color)}>
          {meta.label}
        </span>
      )}
    </div>
  );
}

function GateBlock({ gate, criteria, isAdmin, onApprove }: {
  gate: Phase;
  criteria: Criterion[];
  isAdmin: boolean;
  onApprove: (gateId: string) => void;
}) {
  // Collapsed by default per the waterfall spec.
  const [open, setOpen] = useState(false);

  return (
    <div
      className={cn(
        "rounded-xl border overflow-hidden transition-shadow",
        gate.status === "active" ? "border-amber-300 shadow-card-hover" : "border-border"
      )}
    >
      {/* Gate header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-start gap-3 px-4 py-3 text-left",
          gate.status === "active" ? "bg-amber-50" : gate.status === "complete" ? "bg-emerald-50/40" : "bg-card hover:bg-muted/30"
        )}
      >
        <div className={cn(
          "mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
          gate.status === "complete" ? "bg-emerald-500" : gate.status === "active" ? "bg-amber-500 ring-4 ring-amber-500/20" : "bg-slate-300"
        )}>
          {gate.status === "complete" ? <CheckCircle2 size={14} className="text-white" /> : <Milestone size={12} className="text-white" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="rounded-md bg-amber-500/15 px-1.5 py-0.5 text-micro font-semibold uppercase tracking-wider text-amber-800">Gate</span>
            <span className={cn(
              "text-h4 font-semibold",
              gate.status === "complete" ? "text-emerald-700" : gate.status === "active" ? "text-amber-800" : "text-foreground"
            )}>
              {gate.name.replace(/^Gate:\s*/i, "")}
            </span>
            <span className="text-caption tabular text-muted-foreground">{gate.date}</span>
          </div>
          <div className="mt-0.5 text-caption text-muted-foreground">
            Decision maker · <span className="font-medium text-foreground/80">{gate.owner || "—"}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <PhaseStatusBadge status={gate.status} />
          <ChevronDown size={16} className={cn("text-muted-foreground transition-transform", open && "rotate-180")} />
        </div>
      </button>

      {/* Expanded contents */}
      {open && (
        <div className="space-y-3 border-t border-border bg-background px-4 py-4">
          {/* Structured gate criteria (from Criteria page) */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-micro font-semibold uppercase tracking-wider text-amber-800">Gate criteria</div>
              {criteria.length > 0 && (
                <span className="text-micro text-muted-foreground">
                  {criteria.filter((c) => c.status === "approved").length}/{criteria.length} approved
                </span>
              )}
            </div>
            {criteria.length === 0 ? (
              gate.gateCriteria ? (
                <div className="whitespace-pre-line rounded-md border border-dashed border-amber-300 bg-amber-50/40 px-3 py-2 text-caption text-foreground/90">
                  {gate.gateCriteria}
                  <div className="mt-1 text-micro italic text-muted-foreground">
                    Legacy text criterion. Admins can break this into line items in <span className="font-semibold">Gate Criteria</span>.
                  </div>
                </div>
              ) : (
                <p className="text-caption italic text-muted-foreground">
                  No criteria set.{isAdmin && " Add them on the Gate Criteria page."}
                </p>
              )
            ) : (
              <div className="space-y-1.5">
                {criteria.map((c) => <CriterionLine key={c.id} c={c} canEdit={isAdmin} />)}
              </div>
            )}
          </div>

          {/* Risks / blockers */}
          {gate.risksBlockers && (
            <div className="rounded-md border-l-4 border-rose-400 bg-rose-50/50 px-3 py-2">
              <div className="flex items-center gap-1.5 text-micro font-semibold uppercase tracking-wider text-rose-700">
                <Flag size={11} /> Risks / blockers
              </div>
              <div className="mt-1 whitespace-pre-line text-caption text-foreground/90">{gate.risksBlockers}</div>
            </div>
          )}

          {/* Admin approval */}
          {isAdmin && gate.status !== "complete" && (
            <button
              onClick={() => onApprove(gate.id)}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-caption font-medium text-white hover:bg-emerald-700"
              title="Admin: mark gate complete and advance to the next phase"
            >
              <CheckCircle2 size={13} /> Approve gate & advance
            </button>
          )}

        </div>
      )}
    </div>
  );
}

/** Parse a phase/gate date string into a [start, end] Date range.
 *  Supports "Apr 11", "Apr 15 – May 11", "Wk of Mar 23", "Jun 2". Assumes 2026. */
function parseDateRange(input: string): [Date, Date] | null {
  if (!input) return null;
  const wkMatch = input.match(/Wk of (\w+ \d+)/i);
  if (wkMatch) {
    const d = parseLaunchDate(wkMatch[1]);
    if (!d) return null;
    const end = new Date(d); end.setDate(end.getDate() + 6);
    return [d, end];
  }
  const rangeMatch = input.match(/(\w+ \d+)\s*[–-]\s*(\w+ \d+)/);
  if (rangeMatch) {
    const s = parseLaunchDate(rangeMatch[1]);
    const e = parseLaunchDate(rangeMatch[2]);
    if (s && e) return [s, e];
  }
  const arrowMatch = input.match(/(\w+ \d+)\s*→\s*(\w+ \d+)/);
  if (arrowMatch) {
    const s = parseLaunchDate(arrowMatch[1]);
    const e = parseLaunchDate(arrowMatch[2]);
    if (s && e) return [s, e];
  }
  const single = parseLaunchDate(input);
  if (single) return [single, single];
  return null;
}

const WEEK_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_FMT: Intl.DateTimeFormatOptions = { month: "long", year: "numeric" };

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function eachDay(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  const d = new Date(start);
  while (d <= end) { days.push(new Date(d)); d.setDate(d.getDate() + 1); }
  return days;
}

// Event color tokens — coral for gates, teal for active phase, emerald for
// complete, slate for upcoming. Keeps the palette anchored to Fello's
// primary + accent tokens so nothing feels off-theme.
function eventColors(p: Phase) {
  if (p.isGate) {
    return { bar: "bg-primary/80", ring: "ring-primary/60", dot: "bg-primary", text: "text-primary", pillBg: "bg-primary/10" };
  }
  if (p.status === "complete") {
    return { bar: "bg-emerald-400/70", ring: "ring-emerald-400/50", dot: "bg-emerald-500", text: "text-emerald-700", pillBg: "bg-emerald-50" };
  }
  if (p.status === "active") {
    return { bar: "bg-teal-400/80", ring: "ring-teal-400/60", dot: "bg-teal-500", text: "text-teal-700", pillBg: "bg-teal-50" };
  }
  return { bar: "bg-slate-300", ring: "ring-slate-300", dot: "bg-slate-400", text: "text-slate-600", pillBg: "bg-slate-50" };
}

function CalendarMonth({ monthStart, phases, today }: {
  monthStart: Date;
  phases: { p: Phase; range: [Date, Date] }[];
  today: Date;
}) {
  const firstOfMonth = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
  const lastOfMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const gridEnd = new Date(gridStart);
  gridEnd.setDate(gridEnd.getDate() + 41);

  const cells = eachDay(gridStart, gridEnd);
  const monthIdx = monthStart.getMonth();

  // Events that hit any day in this visible grid
  const eventsForDay = (day: Date) => phases.filter(({ range: [s, e] }) => day >= s && day <= e);

  // Events that hit this month — for the list below the grid
  const monthEvents = phases.filter(
    ({ range: [s, e] }) => e >= firstOfMonth && s <= lastOfMonth
  );

  return (
    <div>
      <h4 className="mb-3 text-center text-caption font-semibold text-muted-foreground">
        {monthStart.toLocaleDateString("en-US", MONTH_FMT)}
      </h4>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 text-center text-micro font-medium text-muted-foreground/70">
        {WEEK_LABELS.map((w) => (
          <div key={w} className="pb-2">{w}</div>
        ))}
      </div>

      {/* Day cells — compact, no grid lines */}
      <div className="grid grid-cols-7 gap-y-2.5">
        {cells.map((day, i) => {
          const inMonth = day.getMonth() === monthIdx;
          const isToday = sameDay(day, today);
          const evs = eventsForDay(day);
          // Highlight any day that is a single-day gate landing on this cell
          const gateAnchor = evs.find(({ p, range: [s, e] }) => p.isGate && sameDay(s, e) && sameDay(day, s));
          // Prefer the "strongest" event for the underline: gate > active > complete > upcoming
          const rank = (p: Phase) => p.isGate ? 0 : p.status === "active" ? 1 : p.status === "complete" ? 2 : 3;
          const sortedEvs = [...evs].sort((a, b) => rank(a.p) - rank(b.p));
          // Show up to 3 bars stacked under the number
          const bars = sortedEvs.slice(0, 3);

          return (
            <div key={i} className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-full text-caption tabular transition-colors",
                  !inMonth && "text-muted-foreground/30",
                  inMonth && !isToday && !gateAnchor && "text-foreground",
                  isToday && "bg-primary font-semibold text-white shadow-sm",
                  gateAnchor && !isToday && "border-2 border-primary font-semibold text-primary"
                )}
              >
                {day.getDate()}
              </div>
              {/* Event bars below the day number — only for in-month days */}
              {inMonth && bars.length > 0 && (
                <div className="flex w-full flex-col items-stretch gap-[2px] px-1">
                  {bars.map(({ p }, idx) => {
                    const c = eventColors(p);
                    return <div key={idx} className={cn("h-[3px] rounded-sm", c.bar)} title={p.name} />;
                  })}
                </div>
              )}
              {/* Spacer so rows without bars keep consistent height */}
              {inMonth && bars.length === 0 && <div className="h-[3px]" />}
            </div>
          );
        })}
      </div>

      {/* Event list for the month */}
      {monthEvents.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {monthEvents.slice(0, 8).map(({ p }, idx) => {
            const c = eventColors(p);
            return (
              <li
                key={`${p.id}-${idx}`}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-caption",
                  c.pillBg,
                  c.text
                )}
                title={p.name}
              >
                {p.isGate ? (
                  <span className={cn("h-2 w-2 shrink-0 rounded-full", c.dot)} />
                ) : (
                  <span className={cn("text-caption leading-none", c.text)}>+</span>
                )}
                <span className="truncate font-medium">{p.name.replace(/^Gate:\s*/i, "")}</span>
              </li>
            );
          })}
          {monthEvents.length > 8 && (
            <li className="text-micro text-muted-foreground">+ {monthEvents.length - 8} more</li>
          )}
        </ul>
      )}
    </div>
  );
}

// Three months per page — matches the reference poster layout
const MONTHS_PER_PAGE = 3;

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function quarterLabel(anchor: Date, count: number): string {
  const end = addMonths(anchor, count - 1);
  const sameYear = anchor.getFullYear() === end.getFullYear();
  const a = anchor.toLocaleDateString("en-US", { month: "short" });
  const b = end.toLocaleDateString("en-US", { month: "short" });
  if (sameYear) return `${a} – ${b} ${end.getFullYear()}`;
  return `${a} ${anchor.getFullYear()} – ${b} ${end.getFullYear()}`;
}

function CalendarView({ phases }: { phases: Phase[] }) {
  const today = useMemo(() => new Date(), []);

  const events = useMemo(() => {
    return phases
      .map((p) => {
        const range = parseDateRange(p.date);
        return range ? { p, range: range as [Date, Date] } : null;
      })
      .filter((x): x is { p: Phase; range: [Date, Date] } => !!x);
  }, [phases]);

  // Anchor the 3-month viewport to the quarter that contains today (or the
  // earliest event if today is outside the launch span).
  const [anchor, setAnchor] = useState<Date>(() => {
    if (events.length === 0) return startOfMonth(today);
    const earliest = new Date(Math.min(...events.map((e) => e.range[0].getTime())));
    const latest = new Date(Math.max(...events.map((e) => e.range[1].getTime())));
    const clampTarget = today < earliest ? earliest : today > latest ? latest : today;
    return startOfMonth(clampTarget);
  });

  const months = useMemo(() => {
    const list: Date[] = [];
    for (let i = 0; i < MONTHS_PER_PAGE; i++) list.push(addMonths(anchor, i));
    return list;
  }, [anchor]);

  if (events.length === 0) {
    return <p className="py-8 text-center text-caption text-muted-foreground">No parseable dates yet.</p>;
  }

  return (
    <div className="space-y-6">
      {/* Quarter header with nav */}
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-h3 font-semibold text-foreground">
          {quarterLabel(anchor, MONTHS_PER_PAGE)}
        </h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setAnchor(addMonths(anchor, -MONTHS_PER_PAGE))}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
            aria-label="Previous quarter"
          >
            <ChevronLeft size={15} />
          </button>
          <button
            type="button"
            onClick={() => setAnchor(startOfMonth(today))}
            className="h-8 rounded-md border border-border bg-card px-3 text-caption font-medium text-foreground hover:border-primary/40"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setAnchor(addMonths(anchor, MONTHS_PER_PAGE))}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
            aria-label="Next quarter"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* Three-month strip */}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-10">
        {months.map((m) => (
          <CalendarMonth key={m.toISOString()} monthStart={m} phases={events} today={today} />
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 border-t border-border pt-4 text-micro text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" /> Gate</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-teal-500" /> Active phase</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Complete</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-slate-400" /> Upcoming</span>
        <span className="flex items-center gap-1.5"><span className="h-4 w-4 rounded-full border-2 border-primary" /> Gate day</span>
        <span className="flex items-center gap-1.5"><span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-white">•</span> Today</span>
      </div>
    </div>
  );
}

export function TimelinePanel() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const PHASES = usePhases();
  const CRITERIA = useCriteria();
  const DELIVERABLES = useDeliverables();
  const [view, setView] = useState<"list" | "calendar">("list");

  const phasesById = useMemo(() => new Map(PHASES.map((p) => [p.id, p])), [PHASES]);
  const tasksByPhase = useMemo(() => {
    const m = new Map<string, Deliverable[]>();
    for (const d of DELIVERABLES) {
      const key = (d.phase ?? "").trim();
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(d);
    }
    return m;
  }, [DELIVERABLES]);

  const criteriaByGate = useMemo(() => {
    const m = new Map<string, Criterion[]>();
    for (const c of CRITERIA) {
      if (!m.has(c.gateId)) m.set(c.gateId, []);
      m.get(c.gateId)!.push(c);
    }
    return m;
  }, [CRITERIA]);

  const handleApprove = async (gateId: string) => {
    if (!confirm("Mark this gate as complete and activate the next phase?")) return;
    // Mark the gate complete
    await fetch(`/api/phases/${gateId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "complete" }),
      credentials: "include",
    });
    // Find the next not-complete phase in list order and activate it
    const idx = PHASES.findIndex((p) => p.id === gateId);
    const next = PHASES.slice(idx + 1).find((p) => p.status !== "complete");
    if (next) {
      await fetch(`/api/phases/${next.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "active" }),
        credentials: "include",
      });
    }
    qc.invalidateQueries({ queryKey: ["phases"] });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Gates & dates</CardTitle>
            <p className="text-caption text-muted-foreground">
              Click a gate to see its phases, criteria, and blockers. Click a phase for its details.
              {isAdmin && " Admins can approve a gate to advance the launch."}
            </p>
          </div>
          <div className="flex gap-1 rounded-lg border border-border bg-muted/40 p-1">
            <button
              onClick={() => setView("list")}
              className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-caption font-medium transition-colors",
                view === "list" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <List size={12} /> List
            </button>
            <button
              onClick={() => setView("calendar")}
              className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-caption font-medium transition-colors",
                view === "calendar" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <CalendarDays size={12} /> Calendar
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {view === "calendar" ? (
          <CalendarView phases={PHASES} />
        ) : (
          <div className="space-y-3">
            {PHASE_SPINE.map((s) => {
              const phase = phasesById.get(s.phaseId);
              const tasks = tasksByPhase.get(s.name) ?? [];
              const gate = s.gateId ? phasesById.get(s.gateId) : undefined;
              const criteria = s.gateId ? (criteriaByGate.get(s.gateId) ?? []) : [];
              return (
                <PhaseContainer
                  key={s.name}
                  phase={phase}
                  tasks={tasks}
                  gate={gate}
                  criteria={criteria}
                  isAdmin={isAdmin}
                  onApprove={handleApprove}
                  defaultOpen={false}
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
